/**
 * AppICEMAP — Arrastrar el mapa aunque el gesto empiece sobre una tarjeta
 *
 * Leaflet detiene a propósito la propagación de `mousedown`/`touchstart` en el
 * contenedor de cada popup (ver Popup._initLayout → disableClickPropagation),
 * para que pulsar dentro de la tarjeta no cierre el popup ni dispare un clic en
 * el mapa. Efecto secundario no buscado: tras `flyToReport` la tarjeta queda
 * justo en el centro de la pantalla, así que el primer gesto de arrastre del
 * usuario casi siempre EMPIEZA sobre ella — y ese `mousedown`/`touchstart`
 * nunca llega al manejador de arrastre del mapa (que escucha en el contenedor
 * del mapa, un ancestro por encima de donde el popup detiene la propagación).
 * El mapa se queda "pegado" al reporte seleccionado hasta que se toca una zona
 * sin tarjeta encima — el bug reportado ("no me deja mover libremente hasta
 * que toco otra cosa").
 *
 * Arreglo: un listener en FASE DE CAPTURA sobre `document` ve el evento antes
 * de que el popup llegue a detener su propagación (la captura baja de la raíz
 * al objetivo antes de que empiece el burbujeo, así que un `stopPropagation`
 * en fase de burbuja no la afecta). Si el objetivo está dentro de un popup y
 * no es uno de sus controles interactivos (el botón de cerrar, un enlace…), se
 * reenvía el evento nativo tal cual al `Draggable` interno de Leaflet
 * (`map.dragging._draggable._onDown`) para que sea Leaflet —no una
 * reimplementación nuestra— quien conduzca el resto del arrastre: límites del
 * mapa, inercia y el `moveend` final salen gratis y ya están probados. A partir
 * de ahí Leaflet engancha sus propios `mousemove`/`mouseup` en `document`, que
 * SÍ reciben los eventos con normalidad — el popup solo bloquea el inicio.
 */

/**
 * @param {import('leaflet').Map} map
 * @returns {() => void} función para desactivar el reenvío
 */
export function enablePopupDragThrough(map) {
	const esControlInteractivo = (target) =>
		!!target.closest?.("a, button, [role='button'], input, textarea");

	const onDown = (e) => {
		const target = e.target;
		if (!(target instanceof Element)) return;
		if (esControlInteractivo(target)) return;
		if (!target.closest(".leaflet-popup")) return;

		const draggable = map.dragging?._draggable;
		if (!draggable) return;

		draggable._onDown(e);
	};

	document.addEventListener("mousedown", onDown, true);
	document.addEventListener("touchstart", onDown, true);

	return () => {
		document.removeEventListener("mousedown", onDown, true);
		document.removeEventListener("touchstart", onDown, true);
	};
}
